import * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import { dateTimeSchema, P, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useIncidentQuery } from '../../graphql/hooks/useIncidentQuery';
import { useUpdateIncidentMutation } from '../../graphql/hooks/useUpdateIncidentMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';
import { IncidentForm } from './IncidentForm';

type FormValues = Common.UpdateIncidentRequest & {
  participant?: {
    id: string;
  } | null;
  reviewers?: string[];
};

const schema: z.ZodSchema<FormValues> = z.object({
  description: z.string('Please enter a description'),
  setting: z.string('Please select a setting'),
  participant: z
    .object({
      id: z.string(),
    })
    .nullish(),
  reviewers: z.array(z.string()).nonempty('Please select at least one reviewer'),
  severity: z.enum(Common.IncidentSeverity, { error: 'Please select incident severity' }),
  ndisReportableIncident: z.boolean(),
  timestamp: dateTimeSchema,
  witnesses: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      email: z.email().nullish(),
      phone: z.string().nullish(),
      description: z.string().nullish(),
    }),
  ),
  labelIds: z.array(z.string()).nullish(),
  categoryId: z.string('Please select a category'),
});

interface Props {
  incidentId: string;
}

export const IncidentEditModal = createModal<Props>((props) => {
  const { modal, incidentId } = props;
  const { errorToast, successToast, organization } = useAppContext();

  const { hide, remove, visible } = modal;

  const { data, isLoading, error } = useIncidentQuery({
    organizationId: organization.id,
    incidentId: props.incidentId,
  });

  const incident = data?.organization?.incident;

  const defaultValues = {
    description: incident?.description,
    participant: incident?.participant,
    reviewers: incident?.reviewers.map((r) => r.id),
    setting: incident?.setting,
    severity: incident?.severity,
    ndisReportableIncident: incident?.ndisReportableIncident,
    timestamp: incident?.timestamp,
    witnesses: incident?.witnesses,
    labelIds: incident?.labels?.map((label) => label.id) ?? [],
    categoryId: incident?.category?.id,
  };

  const methods = useForm<FormValues>({
    defaultValues: defaultValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useUpdateIncidentMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const { participant, reviewers, ...rest } = values;

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          incidentId: incidentId,
          request: {
            ...P.deepTrim(rest),
            participantId: participant?.id,
            reviewerIds: reviewers,
          },
        })
        .then(() => {
          hide();
          successToast('Incident updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating incident: ${err.message}`);
        });
    },
    [mutation, organization.id, incidentId, hide, successToast, errorToast],
  );

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError error={error} />;
    }

    if (!incident) {
      return (
        <PiccoloError
          error={
            new Common.PiccoloError({
              type: 'IncidentNotFound',
              message: 'Incident not found',
            })
          }
        />
      );
    }

    return <IncidentForm />;
  });

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove} size='xl'>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit an incident'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>{content}</ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
